import {Inject, Injectable} from '@angular/core';
import { Observable } from 'rxjs';
import { CoreResponse } from '@models/app/response/CoreResponse';
import { mapRes } from '@shared/utils/data-parsing/http/ResponseMapper.util';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { IFileStorageQueryParams } from '@models/data/file-storage/interfaces/IFileStorageQueryParams';
import { objectToQueryParams } from '@shared/utils/data-parsing/http/ObjectToQueryParams';
import { SpecificResourceMap } from '@models/data/file-storage/enums/SpecificResourceMap.enum';
import { SpecificDirectoryMap } from '@models/data/file-storage/enums/SpecificDirectoryMap.enum';
import { SafeResourceUrl } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import {APP_CONFIG, AppConfig} from '@src/app.config';

@Injectable({
  providedIn: 'root',
})
export class FileStorageHttpService {
  private baseEndpoint = this.appConfig.abpEndpoint + '/fileStorage';

  private endpoints = {
    upload: (params: string) => `${this.baseEndpoint}/upload${params}`,
    uploadMany: (params: string) => `${this.baseEndpoint}/uploadMany${params}`,
    get: (id: string, params: string) => `${this.baseEndpoint}/${id}${params}`,
    delete: (id: string, params: string) =>
      `${this.baseEndpoint}/${id}${params}`,
    deleteMany: () => `${this.baseEndpoint}/deleteMany`,
  };

  private readonly httpOptions = {
    headers: new HttpHeaders(),
    withCredentials: true,
    observe: 'response' as 'response',
  };

  constructor(@Inject(APP_CONFIG) private appConfig: AppConfig, private http: HttpBackend, private domSanitizer: DomSanitizer) {}

  public getFileUrl(
    id: string,
    specificResourceMap: SpecificResourceMap,
    specificDirectoryMap: SpecificDirectoryMap
  ): SafeResourceUrl {
    const queryParams: IFileStorageQueryParams = {
      specificDirectoryMap,
      specificResourceMap,
      date: new Date().getTime().toString(),
    };

    const url = this.endpoints.get(id, objectToQueryParams(queryParams));
    return typeof id !== 'undefined' && id !== null
      ? this.domSanitizer.bypassSecurityTrustResourceUrl(url)
      : null;
  }

  upload(
    file: string | Blob,
    queryParams: IFileStorageQueryParams
  ): Observable<CoreResponse> {
    const params = objectToQueryParams(queryParams);
    const formData = new FormData();
    formData.append('file', file);

    return new HttpClient(this.http)
      .post(this.endpoints.upload(params), formData, this.httpOptions)
      .pipe(mapRes);
  }

  uploadMany(
    files: (string | Blob)[],
    queryParams: IFileStorageQueryParams
  ): Observable<CoreResponse> {
    const params = objectToQueryParams(queryParams);

    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file);
    }

    return new HttpClient(this.http)
      .post(this.endpoints.uploadMany(params), formData, this.httpOptions)
      .pipe(mapRes);
  }

  public delete(
    id: string,
    queryParams: IFileStorageQueryParams
  ): Observable<CoreResponse> {
    return new HttpClient(this.http)
      .delete(
        this.endpoints.delete(id, objectToQueryParams(queryParams)),
        this.httpOptions
      )
      .pipe(mapRes);
  }

  public deleteMany(
    files: string[],
    queryParams: IFileStorageQueryParams
  ): Observable<CoreResponse> {
    return new HttpClient(this.http)
      .post(
        this.endpoints.deleteMany(),
        { ...queryParams, files },
        this.httpOptions
      )
      .pipe(mapRes);
  }
}
